import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

declare const process: { env: Record<string, string | undefined> };
const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh/blend`;

type Row = {
  productId: string; productCode: string; productNumber: string; nameAr: string | null;
  fieldPath: string; flag: string; currentValue: any; suggestedValue: any; editable: boolean;
};

const FLAG_COLORS: Record<string, string> = {
  "مقترح — للمراجعة": "#e8c56a",
  "ترجمة صوتية — للتأكيد": "#8ab4f8",
  "يحتاج إدخال": "var(--bl-danger)",
};
const FIELD_AR: Record<string, string> = {
  nameAr: "الاسم العربي", family: "العائلة", season: "الموسم", occasion: "المناسبة",
  positioning: "التموضع", bottleSizeMl: "حجم الزجاجة ml", packagingSize: "حجم التغليف",
  "classification.barcode": "الباركود", "classification.hsCode": "كود HS",
  "pricing.costEGP": "التكلفة EGP", "pricing.retailPriceEGP": "سعر البيع EGP",
  "pricing.listPriceEGP": "سعر القائمة EGP", "pricing.wholesalePriceEGP": "سعر الجملة EGP",
  "pricing.exportPriceUSD": "سعر التصدير USD",
};

export default function ReviewPage() {
  const [data, setData] = useState<{ total: number; counts: Record<string, number>; rows: Row[] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [flagFilter, setFlagFilter] = useState("");
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState("");

  const load = () =>
    fetch(`${API}/review-queue`, { credentials: "include" })
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.json(); })
      .then(setData).catch((e) => setErr(String(e.message || e)));
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    let out = data.rows;
    if (flagFilter) out = out.filter((r) => r.flag === flagFilter);
    const s = q.trim().toLowerCase();
    if (s) out = out.filter((r) => `${r.productCode} ${r.nameAr} ${r.fieldPath}`.toLowerCase().includes(s));
    return out;
  }, [data, q, flagFilter]);

  const resolve = async (r: Row) => {
    const key = `${r.productId}:${r.fieldPath}`;
    const edited = edits[key];
    const changed = edited !== undefined && edited !== String(r.currentValue ?? "");
    setBusy(key);
    try {
      const res = await fetch(`${API}/review-queue/resolve`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId: r.productId, fieldPath: r.fieldPath,
          action: changed ? "update" : "approve", ...(changed ? { value: edited } : {}) }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(typeof j.detail === "string" ? j.detail : res.statusText);
      toast.success(`${r.productCode} · ${FIELD_AR[r.fieldPath] || r.fieldPath} — ${changed ? "اتحدّث واعتُمد" : "اعتُمد"} ✓`);
      load();
    } catch (e: any) { toast.error(String(e.message || e)); } finally { setBusy(""); }
  };

  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="review-error">{err}</div>;
  if (!data) return <div className="bl-sub py-8 text-center" data-testid="review-loading">جارٍ التحميل…</div>;

  return (
    <div className="bl-card" data-testid="review-page">
      <div className="bl-eyebrow mb-2">🗂 شاشة المراجعة — كل الحقول المعلّمة عبر المنتجات</div>
      <div className="flex gap-2 items-center flex-wrap mb-3">
        <button className={`bl-tab ${flagFilter === "" ? "active" : ""}`} onClick={() => setFlagFilter("")}
          data-testid="review-filter-all">الكل ({data.total})</button>
        {Object.entries(data.counts).map(([f, c]) => (
          <button key={f} className={`bl-tab ${flagFilter === f ? "active" : ""}`} onClick={() => setFlagFilter(f)}
            data-testid={`review-filter-${f.split(" ")[0]}`}>
            <span style={{ color: FLAG_COLORS[f] }}>●</span> {f} ({c})
          </button>
        ))}
        <input className="bl-input" style={{ maxWidth: 220 }} placeholder="ابحث: منتج / حقل…"
          value={q} onChange={(e) => setQ(e.target.value)} data-testid="review-search-input" />
        <span className="bl-sub mr-auto">{rows.length} صف</span>
      </div>
      <table className="bl-table" data-testid="review-table">
        <thead><tr><th>المنتج</th><th>الحقل</th><th>القيمة الحالية</th><th>المقترح/المصدر</th><th>الفلاج</th><th></th></tr></thead>
        <tbody>
          {rows.map((r) => {
            const key = `${r.productId}:${r.fieldPath}`;
            return (
              <tr key={key} data-testid={`review-row-${r.productCode}-${r.fieldPath}`}>
                <td><span className="bl-en font-extrabold">{r.productCode}</span> <span className="bl-sub">{r.nameAr}</span> <span className="bl-sub bl-en">{r.productNumber}</span></td>
                <td>{FIELD_AR[r.fieldPath] || r.fieldPath}</td>
                <td>
                  {r.editable ? (
                    <input className="bl-input" style={{ width: 130 }}
                      value={edits[key] ?? String(r.currentValue ?? "")}
                      placeholder="—"
                      onChange={(e) => setEdits((p) => ({ ...p, [key]: e.target.value }))}
                      data-testid={`review-value-input-${r.productCode}-${r.fieldPath}`} />
                  ) : <span className="bl-sub">{String(r.currentValue ?? "—")}</span>}
                </td>
                <td className="bl-sub" style={{ maxWidth: 260 }}>{String(r.suggestedValue ?? "—")}</td>
                <td><span className="bl-pill" style={{ color: FLAG_COLORS[r.flag] }}>{r.flag}</span></td>
                <td>
                  <button className="bl-btn primary" style={{ padding: "4px 12px" }}
                    disabled={busy === key} onClick={() => resolve(r)}
                    data-testid={`review-approve-${r.productCode}-${r.fieldPath}`}>
                    اعتماد ✓
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="bl-sub mt-2" style={{ fontSize: 10 }}>عدّل القيمة قبل الاعتماد لو محتاجة تصحيح — الاعتماد بيشيل الفلاج ويسجّل مين اعتمد وامتى.</p>
    </div>
  );
}
